import React, { useState } from 'react';
import styled from '@emotion/styled';
import { silver_two, color_shades_darker, color_colors_ocean } from '../../../../../../shared/constants/colors';

interface SearchPickerProps<T> {
    items: T[];
    selectedId: string | null;
    getId: (item: T) => string;
    // Shown in the input while the menu is closed
    selectedLabel: (item: T) => string;
    // Text the search matches against, e.g. name and ID
    searchText: (item: T) => (string | undefined)[];
    renderItem: (item: T) => React.ReactNode;
    onSelect: (item: T) => void;
    placeholder: string;
    ariaLabel: string;
    // For "No offices match", e.g. "offices"
    noun: string;
    disabled?: boolean;
    width?: number;
}

// Type-to-filter picker for lists too long for pills or a plain select (dozens of offices, ~20 configurations)
export function SearchPicker<T>({ items, selectedId, getId, selectedLabel, searchText, renderItem, onSelect, placeholder, ariaLabel, noun, disabled, width }: SearchPickerProps<T>) {
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(0);
    const selected = items.find(item => getId(item) === selectedId);

    const normalizedQuery = query.trim().toLowerCase();
    const matches = normalizedQuery
        ? items.filter(item => searchText(item).some(text => text?.toLowerCase().includes(normalizedQuery)))
        : items;

    const choose = (item: T) => {
        if (getId(item) !== selectedId) onSelect(item);
        setQuery('');
        setIsOpen(false);
    };

    return (
        <PickerWrapper
            width={width}
            onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setIsOpen(false);
                    setQuery('');
                }
            }}
        >
            <PickerInput
                type="text"
                role="combobox"
                aria-label={ariaLabel}
                aria-expanded={isOpen}
                aria-autocomplete="list"
                placeholder={selected ? selectedLabel(selected) : placeholder}
                value={isOpen ? query : (selected ? selectedLabel(selected) : '')}
                disabled={disabled || items.length === 0}
                onFocus={() => { setIsOpen(true); setHighlightIndex(0); }}
                onChange={(e) => { setQuery(e.target.value); setIsOpen(true); setHighlightIndex(0); }}
                onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setHighlightIndex(index => Math.min(index + 1, matches.length - 1));
                    } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setHighlightIndex(index => Math.max(index - 1, 0));
                    } else if (e.key === 'Enter' && matches[highlightIndex]) {
                        e.preventDefault();
                        choose(matches[highlightIndex]);
                    } else if (e.key === 'Escape') {
                        setIsOpen(false);
                        setQuery('');
                        (e.target as HTMLInputElement).blur();
                    }
                }}
            />
            {isOpen && (
                <Menu role="listbox">
                    {matches.length === 0 ? (
                        <MenuEmpty>No {noun} match "{query}"</MenuEmpty>
                    ) : matches.map((item, index) => (
                        <MenuItem
                            key={getId(item)}
                            role="option"
                            type="button"
                            aria-selected={getId(item) === selectedId}
                            selected={getId(item) === selectedId}
                            highlighted={index === highlightIndex}
                            onMouseEnter={() => setHighlightIndex(index)}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => choose(item)}
                        >
                            {renderItem(item)}
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </PickerWrapper>
    );
}

const PickerWrapper = styled.div<{ width?: number }>`
    position: relative;
    width: ${props => props.width ?? 300}px;
    max-width: 100%;

    @media (max-width: 576px) {
        width: 100%;
    }
`;

const PickerInput = styled.input`
    width: 100%;
    height: 34px;
    padding: 8px 36px 8px 16px;
    border: 1px solid ${silver_two};
    border-radius: 20px;
    font-family: Muli;
    font-size: 13px;
    outline: none;
    box-sizing: border-box;
    background-color: #ffffff;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23718096' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3ccircle cx='11' cy='11' r='7'%3e%3c/circle%3e%3cline x1='21' y1='21' x2='16.65' y2='16.65'%3e%3c/line%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 14px center;
    background-size: 13px;
    transition: all 0.2s ease-in-out;

    &::placeholder {
        color: #2d3748;
    }

    &:hover {
        border-color: #cbd5e0;
    }

    &:focus {
        border-color: #007dbb;
        box-shadow: 0 0 0 3px rgba(0, 125, 187, 0.15);

        &::placeholder {
            color: ${color_shades_darker};
        }
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const Menu = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 20;
    max-height: 260px;
    overflow-y: auto;
    padding: 4px;
    background-color: #ffffff;
    border: 1px solid ${silver_two};
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const MenuItem = styled.button<{ selected: boolean; highlighted: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 8px 12px;
    border: none;
    border-radius: 8px;
    background-color: ${props => props.highlighted ? '#f1f5f9' : 'transparent'};
    font-family: Muli;
    font-size: 13px;
    font-weight: ${props => props.selected ? 700 : 400};
    color: ${props => props.selected ? color_colors_ocean : '#2d3748'};
    text-align: left;
    cursor: pointer;
`;

export const MenuSecondaryText = styled.span`
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    font-size: 11px;
    color: ${color_shades_darker};
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const MenuEmpty = styled.div`
    padding: 8px 12px;
    font-family: Muli;
    font-size: 13px;
    font-style: italic;
    color: ${color_shades_darker};
`;
